// ═══════════════════════════════════════════════════════════════════════════
// DOMAIN: quels risques appartiennent à l'écran d'une révision — logique pure
// ═══════════════════════════════════════════════════════════════════════════
//
// **Une révision est la PHOTO de ce qui y a été évalué.** Un risque qui porte
// une entrée reste affiché, quelle que soit l'étape où le projet se trouve
// AUJOURD'HUI.
//
// C'est le défaut corrigé le 2026-08-27. L'écran refiltrait avec
// `project.current_stage`, si bien qu'un projet passé de Cadrage à Build
// escamotait les risques de cadrage de ses propres révisions passées — alors
// que la matrice, elle, les montrait toujours (elle ne lit que les entrées, cf.
// `services/risk_snapshots.py`). Deux écrans, deux populations, et rien à
// l'écran pour expliquer l'écart.
//
// L'étape ne sert plus qu'à UNE chose : sur une révision EN COURS, proposer à
// l'évaluation les risques de l'étape courante qui n'ont pas encore d'entrée.
// C'est ainsi qu'un risque créé après le démarrage de la revue y entre — la
// modale le crée avec l'étape courante, et `save_revision` crée l'entrée
// manquante à l'enregistrement. L'étape ne masque JAMAIS un risque évalué.
//
// Pourquoi ici et pas dans `page-project/risk-table.ts` : la règle y vivait en
// DEUX exemplaires, l'un strict (`computeStats`) et l'autre tolérant aux
// risques sans étape (`sortedRisks`). Ils se contredisaient dès qu'un risque
// n'en portait aucune. Une seule fonction, testée, et le compteur ne peut plus
// diverger de la liste.

// ── Formes minimales, structurelles ───────────────────────────────────────
//
// On ne consomme pas `domain/risk.ts` : ce prédicat n'a besoin que de quatre
// champs, et exiger le `Risk` complet obligerait chaque appelant à s'aligner
// sur des types plus stricts que ce que la règle regarde (`visibility` y est
// une union fermée, `PageRisk` la porte en `string`). Même parti pris que
// `actions-filter.ts`.

/** Ce que la règle regarde d'un risque. Rien de plus. */
export interface RevisionRisk {
  id: number;
  /** Étapes projet où le risque s'applique. Vide → applicable partout. */
  stages?: string[];
  visibility?: string;
  escalated_to_program?: boolean;
}

/** Ce que la règle regarde d'une entrée de révision. */
export interface RevisionRiskEntry {
  /** Identifiant en base, `null` tant que l'entrée n'est pas enregistrée. */
  entryId?: number | null;
  is_relevant?: boolean;
}

/** Ce que l'écran sait de la révision affichée et de ses filtres. */
export interface RevisionViewState {
  /** Révision clôturée : l'étape du jour ne dit plus rien de ce qui a été évalué. */
  closed: boolean;
  /** Étape courante du PROJET — pas celle de la révision, qui n'est pas stockée. */
  currentStage: string | null;
  /** Les entrées de la révision, par identifiant de risque. */
  entries: Record<number, RevisionRiskEntry | undefined>;
  /** 'all' | 'publique' | 'privée'. */
  visibilityFilter?: string;
  showEscalatedOnly?: boolean;
  showNonRelevant?: boolean;
}

/** Le risque a-t-il été évalué dans CETTE révision ?
 *
 *  `entryId` est l'identifiant de la `RevisionEntry` en base. Il vaut `null`
 *  quand la page a fabriqué une entrée par défaut (1 × 1) pour rendre la
 *  saisie possible — c'est ce qui distingue « évalué » de « proposé ».
 */
export function isEvaluated(riskId: number, state: RevisionViewState): boolean {
  const entry = state.entries[riskId];
  return !!entry && entry.entryId != null;
}

/** Hors de l'étape courante.
 *
 *  Notion qui n'a de sens que sur une révision EN COURS : sur une révision
 *  clôturée, ni grisé ni relégation en fin de tri. Un risque sans aucune étape
 *  est applicable partout (cf. `domain/risk.ts`) — il n'est donc jamais hors
 *  étape.
 */
export function isOffStage(risk: RevisionRisk, state: RevisionViewState): boolean {
  if (state.closed || !state.currentStage) return false;
  const stages = risk.stages || [];
  return stages.length > 0 && !stages.includes(state.currentStage);
}

/** Le risque a-t-il sa place sur l'écran de cette révision ?
 *
 *  Règle UNIQUE : le compteur de la barre de stats et la liste affichée
 *  doivent répondre pareil.
 */
export function isRiskVisible(risk: RevisionRisk, state: RevisionViewState): boolean {
  const entry = state.entries[risk.id];
  if (!isEvaluated(risk.id, state)) {
    // Non évalué : il n'a sa place que sur une révision en cours, et seulement
    // s'il relève de l'étape courante.
    if (state.closed) return false;
    if (isOffStage(risk, state)) return false;
  }
  if (
    state.visibilityFilter &&
    state.visibilityFilter !== 'all' &&
    risk.visibility !== state.visibilityFilter
  )
    return false;
  if (state.showEscalatedOnly && !risk.escalated_to_program) return false;
  if (!state.showNonRelevant && entry?.is_relevant === false) return false;
  return true;
}
